import { ClientOptions, SubscriptionClient } from "subscriptions-transport-ws";

export enum Status {
  Disconnected,
  Connecting,
  Reconnecting,
  Connected,
  Reconnected,
}

export interface DisconnectEventDetails {
  code?: number;
  reason?: string;
  wasClean?: boolean;
}

export class SubscriptionClientWithStatus extends SubscriptionClient {
  private _status: Status;
  private disconnectEventHandlers: Array<
    (details: DisconnectEventDetails) => void
  > = [];

  constructor(
    url: string,
    options?: ClientOptions,
    webSocketImpl?: any,
    webSocketProtocols?: string | string[]
  ) {
    super(url, options, webSocketImpl, webSocketProtocols);
    this._status =
      options && options.lazy ? Status.Disconnected : Status.Connecting;
    this.onConnecting(() => (this._status = Status.Connecting));
    this.onConnected(() => {
      this._status = Status.Connected;
      this.attachWebSocketCloseListener();
    });
    this.onReconnecting(() => (this._status = Status.Reconnecting));
    this.onReconnected(() => {
      this._status = Status.Reconnected;
      this.attachWebSocketCloseListener();
    });
    this.onDisconnected(() => (this._status = Status.Disconnected));
  }

  public get connectionStatus(): Status {
    return this._status;
  }

  public onDisconnectEvent(
    callback: (details: DisconnectEventDetails) => void
  ): () => void {
    this.disconnectEventHandlers.push(callback);
    return () => {
      this.disconnectEventHandlers = this.disconnectEventHandlers.filter(
        (cb) => cb !== callback
      );
    };
  }

  private attachWebSocketCloseListener(): void {
    try {
      const wsClient = this.client;

      if (wsClient && !wsClient.__closeListenerAttached) {
        const originalOnClose = wsClient.onclose;

        wsClient.onclose = (event: CloseEvent) => {
          const details: DisconnectEventDetails = {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          };

          this.disconnectEventHandlers.forEach((handler) => {
            try {
              handler(details);
            } catch (error) {
              console.error("Error in disconnect event handler:", error);
            }
          });

          if (originalOnClose) {
            originalOnClose.call(wsClient, event);
          }
        };

        // Mark that we've attached the listener to avoid duplicates
        wsClient.__closeListenerAttached = true;
      }
    } catch (error) {
      console.error("Failed to attach WebSocket close listener:", error);
    }
  }
}
